import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Link2, ExternalLink, Search } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

export default function LinkLibrary() {
  const [q, setQ] = useState("");

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["link_library"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_messages")
        .select("id, group_id, sender_name, content, created_at")
        .ilike("content", "%http%")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data ?? [];
    },
  });

  const links = useMemo(() => {
    const out: { url: string; sender: string | null; date: string; group_id: string | null }[] = [];
    items.forEach((m) => {
      const matches = String(m.content ?? "").match(/https?:\/\/[^\s]+/g) ?? [];
      matches.forEach((url) => out.push({ url, sender: m.sender_name, date: m.created_at, group_id: m.group_id }));
    });
    return out.filter((l) => !q || l.url.toLowerCase().includes(q.toLowerCase()));
  }, [items, q]);

  const domain = (u: string) => { try { return new URL(u).hostname.replace("www.", ""); } catch { return u; } };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Biblioteca de Links" }]} />
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-headline flex items-center gap-2"><Link2 className="w-6 h-6 text-primary" /> Biblioteca de Links</h1>
          <p className="text-sm text-muted-foreground">Todos os links compartilhados nos grupos, agrupados por domínio.</p>
        </div>
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="filtrar por url" className="pl-8" />
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>{links.length} links</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-40 w-full" /> : links.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhum link encontrado.</p>
          ) : (
            <ul className="space-y-2">
              {links.slice(0, 200).map((l, i) => (
                <li key={i} className="flex items-center gap-3 rounded-md border p-2">
                  <Badge variant="outline">{domain(l.url)}</Badge>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm text-primary hover:underline truncate">
                    {l.url}
                  </a>
                  <span className="text-xs text-muted-foreground">{l.sender ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{new Date(l.date).toLocaleDateString("pt-BR")}</span>
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
